import { lazy, type ComponentType } from "react";

/**
 * Session storage key used to track whether a chunk-load reload
 * has already been attempted for the current page load.
 */
const CHUNK_RELOAD_KEY = "chunk-reload-attempted";

/**
 * Maximum number of reload attempts before giving up and
 * letting the error propagate to the nearest ErrorBoundary.
 */
const MAX_RETRIES = 1;

/**
 * Detects whether an error is a chunk/module load failure.
 *
 * Vite and Webpack throw different error types when a dynamic
 * import() fails (usually because the chunk hash changed after
 * a deployment and the user's cached HTML references
 * the old filename).
 *
 * Common patterns:
 * - "Failed to fetch dynamically imported module: …"
 * - "Loading chunk … failed"
 * - "ChunkLoadError"
 * - TypeError with "dynamically imported module" in the message
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  return (
    name === "chunkloaderror" ||
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    (message.includes("failed to fetch") && message.includes("module"))
  );
}

/**
 * A drop-in replacement for React.lazy() that automatically
 * retries chunk loads by triggering a page reload when a stale
 * chunk 404s after a deployment.
 *
 * Safety mechanism:
 * - Uses sessionStorage to track reload attempts
 * - Limits to MAX_RETRIES reloads to prevent infinite loops
 * - After exhausting retries, the error propagates to the nearest
 *   ErrorBoundary for graceful degradation
 *
 * @example
 * ```ts
 * // Before
 * const MyPage = lazy(() => import("./pages/MyPage"));
 *
 * // After
 * const MyPage = lazyWithRetry(() => import("./pages/MyPage"));
 * ```
 */
export function lazyWithRetry<T extends ComponentType<Record<string, unknown>>>(
  importFn: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const component = await importFn();
      // Successful load — clear any previous reload flag
      clearChunkReloadFlag();
      return component;
    } catch (error) {
      if (isChunkLoadError(error)) {
        const attempts = getReloadAttempts();

        if (attempts < MAX_RETRIES) {
          setReloadAttempts(attempts + 1);
          // Force reload from server, bypassing cache
          window.location.reload();
          // Return a promise that never resolves so React doesn't
          // try to render while the page is reloading
          return new Promise<{ default: T }>(() => {
            // intentionally never resolves
          });
        }

        // Already retried — clear the flag and let the error
        // propagate to the ErrorBoundary
        clearChunkReloadFlag();
      }

      throw error;
    }
  });
}

/** Get the current reload attempt count from sessionStorage. */
function getReloadAttempts(): number {
  try {
    const value = sessionStorage.getItem(CHUNK_RELOAD_KEY);
    return value ? parseInt(value, 10) : 0;
  } catch {
    // sessionStorage may be unavailable (e.g. private browsing in some browsers)
    return 0;
  }
}

/** Set the reload attempt count in sessionStorage. */
function setReloadAttempts(count: number): void {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, count.toString());
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}

/** Clear the chunk reload flag from sessionStorage. */
export function clearChunkReloadFlag(): void {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}
